<template>
  <mastery-modal
    :name="modalName"
    type="masteryModalWhite"
    :header-image="headerImage"
    :max-width="372"
    :primary-action-text="primaryText"
    :secondary-action-text="secondaryText"
    @before-open="beforeOpen"
    @close-fn="handleModalClose"
    @primary-action-clicked="begin"
    @secondary-action-clicked="secondaryActionClick"
    @modal-force-closed="handleModalForceClose"
  >
    <template v-if="modalReady">
      <div
        v-if="onIntroScreen"
        class="pre-questionnaire"
      >
        <p class="addon-title">
          {{ stripHtml(catalogPage.addConfig.title || '') }}
        </p>
        <p class="addon-description">
          {{ stripHtml(catalogPage.addConfig.description || '') }}
        </p>
      </div>
      <questionnaire
        v-else
        :clipped-questions="clippedQuestions"
        :allow-back="false"
        :step="step"
        @answer-question="answerQuestion"
        @answer-date-question="answerDateQuestion"
      />
    </template>
  </mastery-modal>
</template>

<script>
import Questionnaire from '@/components/questionnaire/questionnaire.vue';
import MasteryModal from '@/components/ui/masteryModal.vue';

import QuestionnaireMixin from '@/mixins/questionnaire.js';

import { stripHtml } from '@/utils';
import { setupAddonChecklists } from '@/utils/checklist';

export default {
  name: 'CatalogAddModal',
  components: { MasteryModal, Questionnaire },
  mixins: [QuestionnaireMixin],
  data() {
    return {
      catalogPage: null,
      onIntroScreen: false,
      source: null,
    };
  },
  computed: {
    headerImage() {
      if (this.catalogPage && this.onIntroScreen) {
        const attachmentId = this.catalogPage.addConfig.iconId;
        return this.$store.getters['attachments/get'][attachmentId];
      }
    },
    modalName() {
      return 'catalog-add-modal';
    },
    modalReady() {
      return !!this.catalogPage;
    },
    primaryText() {
      if (this.catalogPage && this.onIntroScreen) {
        return this.catalogPage.addConfig.affirmativeButtonText;
      }
    },
    questionnaireId() {
      return this.catalogPage?.questionnaireId;
    },
    secondaryText() {
      if (this.catalogPage && this.onIntroScreen) {
        return this.catalogPage.addConfig.negativeButtonText;
      }
    },
  },
  methods: {
    answerDateQuestion(question, date) {
      const params = this.handleDateAnswer(question, date);
      this.handleUpdateQuestionnaire(params);
    },
    answerQuestion(...args) {
      const params = this.handleQuestionAnswer(...args);
      this.handleUpdateQuestionnaire(params);
    },
    beforeOpen(event) {
      if (event?.params) {
        this.catalogPage = event.params.catalogPage;
        this.onIntroScreen = true;
        this.source = event.params.source;
      }
    },
    begin() {
      this.sendAnalytics(this.primaryText);
      this.onIntroScreen = false;
      this.updateQuestionnaire({}).then(() => {
        this.updateStep(1);
      });
    },
    closeModal() {
      this.$modal.hide(this.modalName);
    },
    handleModalClose() {
      this.catalogPage = null;
      this.source = null;
      this.resetState();
    },
    handleModalForceClose() {
      if (this.startedQuestionnaire) {
        this.$analytics.events.abandonedQuestionnaire(this.questionnaire, this.clippedQuestions[1]);
      }
    },
    handleUpdateQuestionnaire(params) {
      this.updateQuestionnaire(params).then((response) => {
        if (!response.nextQuestion) {
          this.closeModal();
          this.$analytics.events.addedCatalogAddOn(this.catalogPage, this.source);
          const checklistWidgetFlag = this.$apptimize.checkFeatureFlags('checklistWidgetEnabled');
          if (checklistWidgetFlag) {
            setupAddonChecklists(this.$store);
          }
          this.routeToAddonDashboard();
        } else {
          this.updateStep();
        }
      });
    },
    routeToAddonDashboard() {
      // Find the addon purchase receipts
      const receipts = this.catalogPage.activeCatalogPagePurchaseReceipts(this.$store);

      // Find the addon navigation item in the main navigation group and the dashboards that the PO unlocks
      const menuNavGroup = this.$store.getters['navigationGroups/menuNavigationGroup'];
      const addonNavGroupNavItem = menuNavGroup.navigationItems(this.$store).find(ni => ni.destination === 'dashboard_group');

      // Find the navigation group specified in the nav item in the menu nav group
      const addonNavGroup = this.$store.getters['navigationGroups/navigationGroups'][addonNavGroupNavItem?.resourceId];

      // Find the nav item within the above nav group that is a dashboard that is unlocked by the first purchase receipts purchaseOrderId
      const addonDashboardNavItem = addonNavGroup.navigationItems(this.$store).find(ni => {
        if (ni.resourceType == 'AppDashboard') {
          const d = this.$store.getters['dashboards/get'][ni.resourceId];
          return d?.unlockedBy?.includes(receipts[0].purchaseOrderId);
        }
        return false;
      });

      // If the addon navigation item exists and there is at least one dashboard,
      // Route to the dashboard group specified in the navItem and dashboard from the PO
      if (addonNavGroupNavItem && addonDashboardNavItem) {
        this.$router.push({
          name: 'dashboard#group',
          params: {
            dashboardId: addonDashboardNavItem.resourceId,
            navigationGroupId: addonNavGroupNavItem.resourceId,
          },
          query: {
            nav: addonNavGroupNavItem.query,
          },
        });
      }
    },
    secondaryActionClick() {
      this.sendAnalytics(this.secondaryText);
      this.closeModal();
    },
    sendAnalytics(answer) {
      this.$analytics.events.viewedAddOnPrompt(this.catalogPage, this.source, answer);
    },
    stripHtml,
  },
};
</script>

<style lang="scss" scoped>
:deep(.questionnaire-component-container) {
  .content:first-child,
  .content:last-child {
    display: none;
  }

  // overrides the mastery modal .content margin
  .content {
    margin: 0 !important;
  }

  .back {
    top: 50%;
    left: -5%;
  }
}

:deep(.header-image) {
  width: 30%;
}

.pre-questionnaire {
  .addon-title,
  .addon-description {
    margin: 0;
    padding: 0;
  }

  .addon-title {
    font-weight: $bold;
    font-size: rem-calc(26);
  }

  .addon-description {
    line-height: 1.5;
  }
}
</style>
